import type { BiologicalSex } from "@trainwell/features/clients";
import type { SelectionButtonProps } from "src/features/SelectionButtonProps";
import { create } from "zustand";

/**
 * This store manages the state of the user session in the app. It also
 * stores the user's selections on each page.
 */
interface UserState {
  // Temporary user ID created when the user starts the session.
  tempUserId?: string;
  setTempUserId: (userId: string | undefined) => void;
  selections: Record<string, SelectionButtonProps[]>;
  setSelections: (page: string, items?: SelectionButtonProps[]) => void;
  phoneNumber?: string | undefined;
  setPhoneNumber: (phoneNumber: string) => void;
  email?: string | undefined;
  setEmail: (email: string) => void;
  firstName?: string | undefined;
  setFirstName: (firstName: string) => void;
  lastName?: string | undefined;
  setLastName: (lastName: string) => void;
  // User ID that is reflected in the database after the user has
  // been created.
  userId?: string;
  setUserId: (userId: string) => void;

  // Profile information
  profile?: {
    weightSystem: "metric" | "imperial";
    age: string;
    heightInches: string;
    weight: string;
    biologicalSex: BiologicalSex | undefined;
  };
  setProfile: (
    profile:
      | {
          weightSystem: "metric" | "imperial";
          age: string;
          heightInches: string;
          weight: string;
          biologicalSex: BiologicalSex | undefined;
        }
      | undefined,
  ) => void;
}

export const useUserState = create<UserState>((set) => ({
  userId: undefined,
  setUserId: (value) => {
    set({ userId: value });
  },
  tempUserId: undefined,
  setTempUserId: (value: string | undefined) => {
    set({ tempUserId: value });
  },
  selections: {},
  setSelections: (page: string, items?: SelectionButtonProps[]) => {
    set((state) => ({
      selections: {
        ...state.selections,
        [page]: items || [],
      },
    }));
  },
  phoneNumber: undefined,
  setPhoneNumber: (value) => {
    set({ phoneNumber: value });
  },
  email: undefined,
  setEmail: (value) => {
    set({ email: value });
  },
  firstName: undefined,
  setFirstName: (value) => {
    set({ firstName: value });
  },
  lastName: undefined,
  setLastName: (value) => {
    set({ lastName: value });
  },

  // Profile information
  profile: undefined,
  setProfile: (value) => {
    set({ profile: value });
  },
}));
